import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Upload, CheckCircle2, AlertTriangle, Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Badge } from '@/components/ui/Badge';
import { apiClient } from '@/lib/api';
import {
  useProviderAccounts,
  useImportPreview,
  useImportCommit,
  type ImportPreview,
  type UnmatchedPlate,
  type UnmatchedDriver,
} from '@/hooks/useFuel';

interface VehicleOption { id: string; plate_en: string; code: string; }
interface DriverOption  { id: string; full_name_en: string; code: string; }
interface InvestorOption { id: string; full_name_en: string; code: string; }
interface LegalEntityOption { id: string; name_en: string; code: string; }

type Step = 'upload' | 'summary' | 'vehicles' | 'drivers' | 'commit' | 'done';

// New-vehicle form state, keyed by plate_key
interface NewVehicleForm {
  plate_letters_en: string;
  plate_digits: string;
  brand: string;
  model: string;
  fuel_type: 'diesel' | 'gasoline' | 'lpg';
  vehicle_type: string;
  usage_role: 'fleet' | 'manager' | 'admin' | 'personal' | 'workshop';
  investor_id: string;
  legal_entity_id: string;
  petroapp_card_id: string;
  aldrees_card_id: string;
  tank_capacity_l: string;
}

type VehicleResolution =
  | { type: 'link'; vehicle_id: string }
  | { type: 'new'; form: NewVehicleForm }
  | { type: 'skip' };

export default function FuelImportWizard() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('upload');

  // Step 1
  const [file, setFile] = useState<File | null>(null);
  const [providerAccountId, setProviderAccountId] = useState('');
  const [parserHint, setParserHint] = useState('auto');

  // Step 2
  const [preview, setPreview] = useState<ImportPreview | null>(null);

  // Step 3 — vehicle resolutions: keyed by plate_key
  const [vehicleResolutions, setVehicleResolutions] = useState<Record<string, VehicleResolution>>({});

  // Step 4 — driver resolutions: keyed by alias_name
  const [driverResolutions, setDriverResolutions] = useState<Record<string, string | null>>({});

  // Step 5
  const [commitResult, setCommitResult] = useState<any>(null);

  const providerAccounts = useProviderAccounts();
  const previewMut = useImportPreview();
  const commitMut = useImportCommit();

  // Lookup options
  const [vehicles, setVehicles] = useState<VehicleOption[]>([]);
  const [drivers, setDrivers] = useState<DriverOption[]>([]);
  const [investors, setInvestors] = useState<InvestorOption[]>([]);
  const [legalEntities, setLegalEntities] = useState<LegalEntityOption[]>([]);

  // Lookups never block step advancement — if a reference endpoint 4xx/5xxs
  // (e.g. investors pageSize cap, missing perm), we still want to render the
  // Vehicles step and let the user proceed. Each failure surfaces in console.
  async function loadVehicles() {
    if (vehicles.length) return;
    try {
      // projectScope=all → every vehicle across ALL projects (dumper, water,
      // flatbed…). An Aldrees statement mixes all projects, so the picker must
      // show them all, not just the current project's.
      const { data } = await apiClient.get('/api/v1/vehicles', { params: { projectScope: 'all', pageSize: 100 } });
      setVehicles((data.items ?? data ?? []).map((v: any) => ({
        id: v.id, plate_en: v.plate_en ?? v.plateEn ?? '', code: v.code,
      })));
    } catch (err) {
      console.error('FuelImport: loadVehicles failed', err);
      setVehicles([]);
    }
  }
  async function loadDrivers() {
    if (drivers.length) return;
    try {
      const { data } = await apiClient.get('/api/v1/drivers', { params: { pageSize: 500 } });
      setDrivers((data.items ?? data ?? []).map((d: any) => ({
        id: d.id, full_name_en: d.full_name_en ?? d.fullNameEn ?? '', code: d.code,
      })));
    } catch (err) {
      console.error('FuelImport: loadDrivers failed', err);
      setDrivers([]);
    }
  }
  async function loadFormReferenceData() {
    if (investors.length === 0) {
      try {
        const { data } = await apiClient.get('/api/v1/investors', { params: { pageSize: 500 } });
        setInvestors((data.items ?? data ?? []).map((i: any) => ({
          id: i.id, full_name_en: i.full_name_en ?? i.fullNameEn ?? '', code: i.code,
        })));
      } catch (err) {
        console.error('FuelImport: loadInvestors failed', err);
        setInvestors([]);
      }
    }
    if (legalEntities.length === 0) {
      try {
        const { data } = await apiClient.get('/api/v1/legal-entities');
        setLegalEntities((data.items ?? data ?? []).map((l: any) => ({
          id: l.id, name_en: l.name_en ?? l.nameEn ?? '', code: l.code,
        })));
      } catch (err) {
        console.error('FuelImport: loadLegalEntities failed', err);
        setLegalEntities([]);
      }
    }
  }

  async function doPreview() {
    if (!file || !providerAccountId) return;
    try {
      const result = await previewMut.mutateAsync({
        file, provider_account_id: providerAccountId, parser_hint: parserHint,
      });
      setPreview(result);
      // Pre-fill: generic-placeholder drivers default to "leave unmapped" (null)
      const dr: Record<string, string | null> = {};
      for (const d of result.summary.unique_unmatched_drivers) {
        if (d.is_generic_placeholder) dr[d.alias_name] = null;
      }
      setDriverResolutions(dr);
      setStep('summary');
    } catch (err: any) {
      alert(err?.response?.data?.error ?? err?.message ?? 'Preview failed');
    }
  }

  async function doCommit() {
    if (!preview) return;
    // Validate all "new" vehicle forms have required fields
    const new_vehicles: any[] = [];
    const vehicle_resolutions: Record<string, string | null> = {};

    for (const [plate_key, res] of Object.entries(vehicleResolutions)) {
      if (res.type === 'link') {
        // A "link" with no vehicle chosen would send an empty string and be
        // rejected by the server. Treat an unresolved link as a skip.
        vehicle_resolutions[plate_key] = res.vehicle_id ? res.vehicle_id : null;
      } else if (res.type === 'skip') {
        vehicle_resolutions[plate_key] = null;
      } else if (res.type === 'new') {
        const f = res.form;
        if (!f.plate_letters_en || !f.plate_digits || !f.investor_id || !f.legal_entity_id) {
          alert(t('fuel.import.newVehicleIncomplete', { plate: plate_key }));
          return;
        }
        new_vehicles.push({
          plate_letters_en: f.plate_letters_en,
          plate_digits: f.plate_digits,
          plate_en: `${f.plate_digits} ${f.plate_letters_en}`,
          brand: f.brand || null,
          model: f.model || null,
          fuel_type: f.fuel_type,
          vehicle_type: f.vehicle_type,
          investor_id: f.investor_id,
          legal_entity_id: f.legal_entity_id,
          petroapp_card_id: f.petroapp_card_id || null,
          aldrees_card_id: f.aldrees_card_id || null,
          tank_capacity_l: f.tank_capacity_l ? Number(f.tank_capacity_l) : null,
          usage_role: f.usage_role,
          resolves_plate_key: plate_key,
        });
      }
    }

    try {
      const result = await commitMut.mutateAsync({
        import_id: preview.import_id,
        driver_resolutions: driverResolutions,
        vehicle_resolutions,
        new_vehicles,
        new_drivers: [],
      });
      setCommitResult(result);
      setStep('done');
    } catch (err: any) {
      alert(err?.response?.data?.message ?? err?.message ?? 'Commit failed');
    }
  }

  const fmt = (n: number) => n.toLocaleString();

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard/fuel')} className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            {t('common.back')}
          </Button>
          <h1 className="text-lg font-semibold">{t('fuel.import.title')}</h1>
          <div className="ml-auto flex items-center gap-1 text-xs text-muted-foreground">
            {(['upload', 'summary', 'vehicles', 'drivers', 'commit', 'done'] as Step[]).map((s, i) => (
              <span
                key={s}
                className={`px-2 py-1 rounded ${step === s ? 'bg-gold/20 text-gold-foreground' : 'opacity-60'}`}
              >
                {i + 1}. {t(`fuel.import.step.${s}`)}
              </span>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6">
        {/* ─── STEP 1 ─── */}
        {step === 'upload' && (
          <div className="space-y-4">
            <div>
              <Label>{t('fuel.import.providerAccount')}</Label>
              <select
                className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                value={providerAccountId}
                onChange={(e) => setProviderAccountId(e.target.value)}
              >
                <option value="">— {t('common.select')} —</option>
                {(providerAccounts.data ?? []).map((a) => (
                  <option key={a.id} value={a.id}>[{a.provider}] {a.account_label}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>{t('fuel.import.parserHint')}</Label>
              <select
                className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                value={parserHint}
                onChange={(e) => setParserHint(e.target.value)}
              >
                <option value="auto">{t('fuel.import.parserAuto')}</option>
                <option value="petroapp">PetroApp bills</option>
                <option value="aldrees_fleetwise">Aldrees Fleet-wise</option>
                <option value="aldrees_daily">Aldrees Daily</option>
                <option value="aldrees_statement">Aldrees Monthly Statement</option>
              </select>
            </div>
            <div>
              <Label>{t('fuel.import.file')}</Label>
              <Input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                className="mt-1"
              />
              {file ? <div className="mt-1 text-xs text-muted-foreground">{file.name} · {Math.round(file.size / 1024)} KB</div> : null}
            </div>
            <Button
              disabled={!file || !providerAccountId || previewMut.isPending}
              onClick={doPreview}
              className="gap-2 bg-gold text-gold-foreground hover:bg-gold/90"
            >
              <Upload className="h-4 w-4" />
              {previewMut.isPending ? t('common.processing') : t('fuel.import.uploadAndPreview')}
            </Button>
          </div>
        )}

        {/* ─── STEP 2 — summary ─── */}
        {step === 'summary' && preview && (
          <div className="space-y-4">
            <div className="rounded-md border border-border bg-card p-4">
              <h3 className="font-medium mb-2">{t('fuel.import.previewHeader')}</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <Stat label={t('fuel.import.detectedProvider')} value={preview.detected_provider} />
                <Stat label={t('fuel.import.period')} value={preview.period_from && preview.period_to
                  ? `${preview.period_from.slice(0, 10)} → ${preview.period_to.slice(0, 10)}` : '—'} />
                <Stat label={t('fuel.import.totalRows')} value={fmt(preview.summary.total_rows)} />
                <Stat label={t('fuel.import.newRows')} value={fmt(preview.summary.new_rows)} />
                <Stat label={t('fuel.import.duplicateRows')} value={fmt(preview.summary.duplicate_rows)} warn={preview.summary.duplicate_rows > 0} />
                <Stat label={t('fuel.import.unmatchedVehicles')} value={fmt(preview.summary.unique_unmatched_plates.length)} warn={preview.summary.unique_unmatched_plates.length > 0} />
                <Stat label={t('fuel.import.unmatchedDrivers')} value={fmt(preview.summary.unique_unmatched_drivers.length)} warn={preview.summary.unique_unmatched_drivers.length > 0} />
                <Stat label={t('fuel.import.cardMovements')} value={fmt(preview.summary.card_movements_flagged)} warn={preview.summary.card_movements_flagged > 0} />
              </div>
            </div>
            {preview.warnings.length > 0 ? (
              <div className="rounded-md border border-amber-400 bg-amber-50 dark:bg-amber-500/10 p-3">
                <div className="flex items-center gap-2 text-amber-800 dark:text-amber-300 text-sm font-medium">
                  <AlertTriangle className="h-4 w-4" /> {t('fuel.import.warnings')}
                </div>
                <ul className="mt-2 text-xs text-amber-700 dark:text-amber-200/80 list-disc list-inside">
                  {preview.warnings.slice(0, 5).map((w, i) => <li key={i}>{w}</li>)}
                  {preview.warnings.length > 5 ? <li>… {preview.warnings.length - 5} {t('common.more')}</li> : null}
                </ul>
              </div>
            ) : null}
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep('upload')}>{t('common.back')}</Button>
              <Button onClick={async () => { await Promise.all([loadVehicles(), loadFormReferenceData()]); setStep('vehicles'); }}
                className="bg-gold text-gold-foreground hover:bg-gold/90">{t('fuel.import.continue')}</Button>
            </div>
          </div>
        )}

        {/* ─── STEP 3 — vehicle linker (now with inline create) ─── */}
        {step === 'vehicles' && preview && (
          <VehicleLinker
            unmatched={preview.summary.unique_unmatched_plates}
            vehicles={vehicles}
            investors={investors}
            legalEntities={legalEntities}
            providerProvider={preview.detected_provider}
            resolutions={vehicleResolutions}
            setResolutions={setVehicleResolutions}
            onBack={() => setStep('summary')}
            onNext={() => { loadDrivers(); setStep('drivers'); }}
          />
        )}

        {/* ─── STEP 4 — driver linker ─── */}
        {step === 'drivers' && preview && (
          <DriverLinker
            unmatched={preview.summary.unique_unmatched_drivers}
            drivers={drivers}
            resolutions={driverResolutions}
            setResolutions={setDriverResolutions}
            onBack={() => setStep('vehicles')}
            onNext={() => setStep('commit')}
          />
        )}

        {/* ─── STEP 5 — commit ─── */}
        {step === 'commit' && preview && (
          <div className="space-y-4">
            <div className="rounded-md border border-border bg-card p-4">
              <h3 className="font-medium mb-2">{t('fuel.import.confirmHeader')}</h3>
              <ul className="text-sm space-y-1">
                <li>✓ {t('fuel.import.willInsert', { n: preview.summary.new_rows })}</li>
                <li>✓ {t('fuel.import.willSkipDup', { n: preview.summary.duplicate_rows })}</li>
                <li>✓ {t('fuel.import.willCreateVehicles', { n: Object.values(vehicleResolutions).filter(r => r.type === 'new').length })}</li>
                <li>✓ {t('fuel.import.willMapVehicles', { n: Object.values(vehicleResolutions).filter(r => r.type === 'link').length })}</li>
                <li>✓ {t('fuel.import.willMapDrivers', { n: Object.values(driverResolutions).filter(Boolean).length })}</li>
                {preview.summary.card_movements_flagged > 0 ? (
                  <li className="text-amber-700 dark:text-amber-300">⚠ {t('fuel.import.willFlagMovements', { n: preview.summary.card_movements_flagged })}</li>
                ) : null}
              </ul>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep('drivers')}>{t('common.back')}</Button>
              <Button disabled={commitMut.isPending} onClick={doCommit}
                className="bg-gold text-gold-foreground hover:bg-gold/90">
                {commitMut.isPending ? t('common.processing') : t('fuel.import.commitButton')}
              </Button>
            </div>
          </div>
        )}

        {/* ─── DONE ─── */}
        {step === 'done' && commitResult && (
          <div className="space-y-4">
            <div className="rounded-md border border-emerald-300 bg-emerald-50 dark:bg-emerald-500/10 dark:border-emerald-500/40 p-6 text-center">
              <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-600 mb-2" />
              <h3 className="text-lg font-semibold text-emerald-800 dark:text-emerald-300">{t('fuel.import.successTitle')}</h3>
              <p className="text-sm text-emerald-700 dark:text-emerald-200/80 mt-2">
                {t('fuel.import.successInserted', { n: commitResult.inserted_rows })}
              </p>
              {commitResult.new_vehicles > 0 ? (
                <p className="text-sm text-emerald-700 dark:text-emerald-200/80 mt-1">
                  +{commitResult.new_vehicles} {t('fuel.import.newVehiclesCreated')}
                </p>
              ) : null}
              {commitResult.card_movements_created > 0 ? (
                <p className="text-sm text-amber-700 dark:text-amber-300 mt-2">⚠ {t('fuel.import.successMovements', { n: commitResult.card_movements_created })}</p>
              ) : null}
            </div>
            <div className="flex gap-2">
              <Link to="/dashboard/fuel"><Button>{t('fuel.import.viewTransactions')}</Button></Link>
              {commitResult.card_movements_created > 0 ? (
                <Link to="/dashboard/fuel/card-movements">
                  <Button variant="outline">{t('fuel.import.reviewMovements')}</Button>
                </Link>
              ) : null}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function Stat({ label, value, warn }: { label: string; value: any; warn?: boolean }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`mt-0.5 ${warn ? 'text-amber-700 font-medium' : ''}`}>{value}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// VEHICLE LINKER — with three modes per row: link / new / skip
// ─────────────────────────────────────────────────────────────────
function VehicleLinker({
  unmatched, vehicles, investors, legalEntities, providerProvider,
  resolutions, setResolutions, onBack, onNext,
}: {
  unmatched: UnmatchedPlate[];
  vehicles: VehicleOption[];
  investors: InvestorOption[];
  legalEntities: LegalEntityOption[];
  providerProvider: string;
  resolutions: Record<string, VehicleResolution>;
  setResolutions: (r: Record<string, VehicleResolution>) => void;
  onBack: () => void;
  onNext: () => void;
}) {
  const { t } = useTranslation();

  if (unmatched.length === 0) {
    return (
      <div className="space-y-4">
        <div className="rounded-md border border-emerald-300 bg-emerald-50 dark:bg-emerald-500/10 p-4 text-sm text-emerald-800 dark:text-emerald-300">
          ✓ {t('fuel.import.allVehiclesMatched')}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onBack}>{t('common.back')}</Button>
          <Button onClick={onNext} className="bg-gold text-gold-foreground hover:bg-gold/90">{t('common.continue')}</Button>
        </div>
      </div>
    );
  }

  function setMode(plate_key: string, mode: 'link' | 'new' | 'skip', plate_raw?: string, hint?: { brand: string | null; model: string | null }) {
    if (mode === 'skip') {
      setResolutions({ ...resolutions, [plate_key]: { type: 'skip' } });
    } else if (mode === 'link') {
      setResolutions({ ...resolutions, [plate_key]: { type: 'link', vehicle_id: '' } });
    } else {
      // 'new' — initialize a blank form, parsing letters/digits from the plate hint
      const cleaned = (plate_raw ?? plate_key).replace(/[^A-Za-z0-9]/g, '').toUpperCase();
      const letters = cleaned.replace(/[0-9]/g, '');
      const digits = cleaned.replace(/[A-Z]/g, '');
      setResolutions({
        ...resolutions,
        [plate_key]: {
          type: 'new',
          form: {
            plate_letters_en: letters,
            plate_digits: digits,
            brand: hint?.brand ?? '',
            model: hint?.model ?? '',
            fuel_type: 'diesel',
            vehicle_type: 'truck',
            usage_role: 'fleet',
            investor_id: '',
            legal_entity_id: legalEntities[0]?.id ?? '',
            petroapp_card_id: providerProvider === 'petroapp' ? '' : '',
            aldrees_card_id: providerProvider === 'petroapp' ? '' : '',
            tank_capacity_l: '',
          },
        },
      });
    }
  }

  function updateForm(plate_key: string, patch: Partial<NewVehicleForm>) {
    const cur = resolutions[plate_key];
    if (cur?.type !== 'new') return;
    setResolutions({
      ...resolutions,
      [plate_key]: { type: 'new', form: { ...cur.form, ...patch } },
    });
  }

  return (
    <div className="space-y-4">
      <div className="text-sm text-muted-foreground">{t('fuel.import.linkVehiclesHintV2')}</div>
      <div className="space-y-3">
        {unmatched.map((u) => {
          const res = resolutions[u.plate_key];
          return (
            <div key={u.plate_key} className="rounded-md border border-border bg-card overflow-hidden">
              <div className="flex items-center gap-3 p-3 border-b border-border bg-muted/20">
                <div className="flex-1">
                  <div className="font-mono text-sm">{u.plate_raw}</div>
                  <div className="text-xs text-muted-foreground">
                    {u.sample_brand ?? '—'} · {u.sample_model ?? '—'} · {t('fuel.import.timesAppeared', { n: u.sample_count })}
                  </div>
                </div>
                <div className="flex items-center gap-1 text-xs">
                  <ModeBtn active={res?.type === 'link'} onClick={() => setMode(u.plate_key, 'link')}>{t('fuel.import.modeLink')}</ModeBtn>
                  <ModeBtn active={res?.type === 'new'} onClick={() => setMode(u.plate_key, 'new', u.plate_raw, { brand: u.sample_brand, model: u.sample_model })}>{t('fuel.import.modeNew')}</ModeBtn>
                  <ModeBtn active={res?.type === 'skip'} onClick={() => setMode(u.plate_key, 'skip')}>{t('fuel.import.modeSkip')}</ModeBtn>
                </div>
              </div>

              {/* Mode body */}
              {res?.type === 'link' ? (
                <div className="p-3">
                  {/* Searchable combobox: type any part of the plate or code.
                      Native datalist filters as you type across ALL vehicles. */}
                  <input
                    list={`veh-list-${u.plate_key}`}
                    className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
                    placeholder={t('fuel.import.searchVehicle', { defaultValue: 'Type plate or code to search all vehicles…' })}
                    defaultValue={res.vehicle_id ? (() => { const v = vehicles.find((x) => x.id === res.vehicle_id); return v ? `${v.plate_en} (${v.code})` : ''; })() : ''}
                    onChange={(e) => {
                      const val = e.target.value.trim();
                      const v = vehicles.find((x) => `${x.plate_en} (${x.code})` === val
                        || x.plate_en === val || x.code === val
                        || x.plate_en.replace(/[^A-Za-z0-9]/g, '').toUpperCase() === val.replace(/[^A-Za-z0-9]/g, '').toUpperCase());
                      setResolutions({ ...resolutions, [u.plate_key]: { type: 'link', vehicle_id: v ? v.id : '' } });
                    }}
                  />
                  <datalist id={`veh-list-${u.plate_key}`}>
                    {vehicles.map((v) => <option key={v.id} value={`${v.plate_en} (${v.code})`} />)}
                  </datalist>
                  {!res.vehicle_id ? (
                    <div className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                      {t('fuel.import.pickVehicleHint', { defaultValue: 'Pick a vehicle from the list, or choose Skip. Empty links are skipped.' })}
                    </div>
                  ) : (
                    <div className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">✓ {t('common.linked', { defaultValue: 'Linked' })}</div>
                  )}
                </div>
              ) : null}

              {res?.type === 'new' ? (
                <NewVehicleSubForm
                  form={res.form}
                  investors={investors}
                  legalEntities={legalEntities}
                  providerProvider={providerProvider}
                  onChange={(patch) => updateForm(u.plate_key, patch)}
                />
              ) : null}

              {res?.type === 'skip' ? (
                <div className="p-3 text-xs text-muted-foreground italic">
                  {t('fuel.import.skipNote')}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      <div className="flex gap-2">
        <Button variant="outline" onClick={onBack}>{t('common.back')}</Button>
        <Button onClick={onNext} className="bg-gold text-gold-foreground hover:bg-gold/90">
          {t('common.continue')}
        </Button>
      </div>
    </div>
  );
}

function ModeBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: any }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-2.5 py-1 rounded-md border transition-colors ${
        active ? 'border-gold bg-gold/10 text-foreground font-medium' : 'border-border bg-background hover:bg-accent/50'
      }`}
    >
      {children}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────
// NEW VEHICLE SUB-FORM
// ─────────────────────────────────────────────────────────────────
function NewVehicleSubForm({
  form, investors, legalEntities, providerProvider, onChange,
}: {
  form: NewVehicleForm;
  investors: InvestorOption[];
  legalEntities: LegalEntityOption[];
  providerProvider: string;
  onChange: (patch: Partial<NewVehicleForm>) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="p-3 grid grid-cols-2 gap-3 bg-emerald-50/30 dark:bg-emerald-500/5">
      <Field label={t('fuel.import.newVeh.plateLetters') + ' *'}>
        <Input value={form.plate_letters_en} onChange={(e) => onChange({ plate_letters_en: e.target.value.toUpperCase() })} placeholder="EJU" />
      </Field>
      <Field label={t('fuel.import.newVeh.plateDigits') + ' *'}>
        <Input value={form.plate_digits} onChange={(e) => onChange({ plate_digits: e.target.value.replace(/\D/g, '') })} placeholder="4456" />
      </Field>
      <Field label={t('fuel.import.newVeh.brand')}>
        <Input value={form.brand} onChange={(e) => onChange({ brand: e.target.value })} placeholder="Volvo" />
      </Field>
      <Field label={t('fuel.import.newVeh.model')}>
        <Input value={form.model} onChange={(e) => onChange({ model: e.target.value })} placeholder="FH16" />
      </Field>
      <Field label={t('fuel.import.newVeh.investor') + ' *'}>
        <select className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
          value={form.investor_id} onChange={(e) => onChange({ investor_id: e.target.value })}>
          <option value="">— {t('common.select')} —</option>
          {investors.map((i) => <option key={i.id} value={i.id}>{i.full_name_en} ({i.code})</option>)}
        </select>
      </Field>
      <Field label={t('fuel.import.newVeh.legalEntity') + ' *'}>
        <select className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
          value={form.legal_entity_id} onChange={(e) => onChange({ legal_entity_id: e.target.value })}>
          <option value="">— {t('common.select')} —</option>
          {legalEntities.map((l) => <option key={l.id} value={l.id}>{l.name_en} ({l.code})</option>)}
        </select>
      </Field>
      <Field label={t('fuel.import.newVeh.fuelType')}>
        <select className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
          value={form.fuel_type} onChange={(e) => onChange({ fuel_type: e.target.value as any })}>
          <option value="diesel">Diesel</option>
          <option value="gasoline">Gasoline</option>
          <option value="lpg">LPG</option>
        </select>
      </Field>
      <Field label={t('fuel.import.newVeh.vehicleType')}>
        <select className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
          value={form.vehicle_type} onChange={(e) => onChange({ vehicle_type: e.target.value })}>
          <option value="truck">Truck</option>
          <option value="tanker_water">Tanker (water)</option>
          <option value="tanker_sewage">Tanker (sewage)</option>
          <option value="container_truck">Container truck</option>
          <option value="flatbed">Flatbed</option>
          <option value="manager_car">Manager car</option>
          <option value="other">Other</option>
        </select>
      </Field>
      <Field label={t('fuel.import.newVeh.tankCapacity')}>
        <Input type="number" value={form.tank_capacity_l} onChange={(e) => onChange({ tank_capacity_l: e.target.value })} placeholder="400" />
      </Field>
      <Field label={t('fuel.import.newVeh.usageRole')}>
        <select className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
          value={form.usage_role} onChange={(e) => onChange({ usage_role: e.target.value as any })}>
          <option value="fleet">{t('fuel.import.newVeh.roleFleet')}</option>
          <option value="manager">{t('fuel.import.newVeh.roleManager')}</option>
          <option value="admin">{t('fuel.import.newVeh.roleAdmin')}</option>
          <option value="personal">{t('fuel.import.newVeh.rolePersonal')}</option>
          <option value="workshop">{t('fuel.import.newVeh.roleWorkshop')}</option>
        </select>
      </Field>
      {providerProvider === 'petroapp' ? (
        <Field label="PetroApp card">
          <Input value={form.petroapp_card_id} onChange={(e) => onChange({ petroapp_card_id: e.target.value })} placeholder="optional" />
        </Field>
      ) : (
        <Field label="Aldrees card">
          <Input value={form.aldrees_card_id} onChange={(e) => onChange({ aldrees_card_id: e.target.value })} placeholder="optional" />
        </Field>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: any }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DRIVER LINKER — same as before
// ─────────────────────────────────────────────────────────────────
function DriverLinker({
  unmatched, drivers, resolutions, setResolutions, onBack, onNext,
}: {
  unmatched: UnmatchedDriver[];
  drivers: DriverOption[];
  resolutions: Record<string, string | null>;
  setResolutions: (r: Record<string, string | null>) => void;
  onBack: () => void;
  onNext: () => void;
}) {
  const { t } = useTranslation();
  if (unmatched.length === 0) {
    return (
      <div className="space-y-4">
        <div className="rounded-md border border-emerald-300 bg-emerald-50 dark:bg-emerald-500/10 p-4 text-sm text-emerald-800 dark:text-emerald-300">
          ✓ {t('fuel.import.allDriversMatched')}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onBack}>{t('common.back')}</Button>
          <Button onClick={onNext} className="bg-gold text-gold-foreground hover:bg-gold/90">{t('common.continue')}</Button>
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <div className="text-sm text-muted-foreground">{t('fuel.import.linkDriversHint')}</div>
      <div className="space-y-2">
        {unmatched.map((u) => (
          <div key={u.alias_name} className="flex items-center gap-3 rounded-md border border-border bg-card p-3">
            <div className="flex-1">
              <div className="text-sm">{u.alias_name}</div>
              <div className="text-xs text-muted-foreground">
                {t('fuel.import.timesAppeared', { n: u.sample_count })}
                {u.is_generic_placeholder ? <Badge variant="inactive" className="ml-2 text-xs">{t('fuel.import.generic')}</Badge> : null}
              </div>
            </div>
            <select
              className="min-w-[220px] h-9 rounded-md border border-input bg-background px-2 text-sm"
              value={resolutions[u.alias_name] === null ? '__unmapped__' : (resolutions[u.alias_name] ?? '')}
              onChange={(e) => {
                const val = e.target.value;
                setResolutions({
                  ...resolutions,
                  [u.alias_name]: val === '__unmapped__' ? null : (val || undefined as any),
                });
              }}
            >
              <option value="">— {t('common.select')} —</option>
              <option value="__unmapped__">{t('fuel.import.leaveUnmapped')}</option>
              {drivers.map((d) => <option key={d.id} value={d.id}>{d.full_name_en} ({d.code})</option>)}
            </select>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <Button variant="outline" onClick={onBack}>{t('common.back')}</Button>
        <Button onClick={onNext} className="bg-gold text-gold-foreground hover:bg-gold/90">{t('common.continue')}</Button>
      </div>
    </div>
  );
}
